import type { License } from '../api/types';
import {
  elapsedFraction,
  formatDate,
  formatRemaining,
  licenseLabel,
  licenseState,
} from '../lib/license';

/**
 * The console's one committed element.
 *
 * Every other licensing tool prints two dates and leaves the arithmetic to
 * you. A license IS a length of time, so it is drawn as one: the track is the
 * whole period, the fill is how much has elapsed, and the label says what is
 * left. Whether a customer is locked out becomes something you read, not
 * something you compute.
 */
export function Runway({
  license,
  size = 'sm',
  showDate = true,
}: {
  license: License;
  size?: 'sm' | 'lg';
  showDate?: boolean;
}) {
  const state = licenseState(license);
  const elapsed = elapsedFraction(license);
  const remaining = formatRemaining(license);

  return (
    <div
      className={`runway runway--${state}${size === 'lg' ? ' runway--lg' : ''}`}
      // One sentence for assistive tech; the bar is decorative on its own.
      title={`${licenseLabel(license)} — ${remaining}, expires ${formatDate(license.expires_at)}`}
    >
      <div className="runway__track">
        <div
          className="runway__fill"
          style={{ transform: `scaleX(${elapsed.toFixed(4)})` }}
          aria-hidden
        />
      </div>
      <div className="runway__labels">
        <span className="runway__remaining">{remaining}</span>
        {showDate && <span className="runway__date">{formatDate(license.expires_at)}</span>}
      </div>
    </div>
  );
}

/**
 * Status as text plus a dot — never colour alone, so it survives a colourblind
 * reader and a monochrome print.
 */
export function StatusPill({ license }: { license: License }) {
  const state = license.status === 'revoked' ? 'suspended' : licenseState(license);
  return <span className={`pill pill--${state}`}>{licenseLabel(license)}</span>;
}

export function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: 'live' | 'expiring' | 'expired' | 'suspended' | 'pending' | 'neutral';
  children: React.ReactNode;
}) {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}
